import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { controlToastState, openModal } from "risa-oasis-ui_v2";
import { updateCPTtables } from "../../../../api/postCall/updateCPTtables";
import CustomTable from "../../../../components/custom-table/custom-table";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import UpdateCigNoteConfirmationDialog from "../../../../components/modals/updateCigNoteConfirmationDialog/updateCigNoteConfirmationDialog";
import { LocalStorageKeys } from "../../../../enums/localStorageKeys";

import moment from "moment";
import { FirestoreService } from "../../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../api/firebase/references";
import AuthInaccuracyModal from "../../../../components/modals/authInaccuracyModal/authInaccuracyModal";
import DraftSubmissionModal from "../../../../components/modals/draftSubmissionModal/draftSubmissionModal";
import StatusChangedToHoldModal from "../../../../components/modals/statusChangedtoHoldModal/statusChangedToHoldModal";
import { StatusUpdateModal } from "../../../../components/modals/StatusUpdateModal/StatusUpdateModal";
import { useBusniessOfficeNotesContext } from "../../../../context/busniessOfficeNotesContext";
import {
  CptCodesTableRowModel,
  PaRequired,
} from "../../../../data-model/cptCodesTableModel";
import { MedicalPaOrder } from "../../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { ModalId } from "../../../../enums/modalId";
import { CptCodeWithDetailsKeys } from "../../../../enums/tableColumnKeys";
import {
  closeModal,
  setOpenedModalId,
} from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import {
  getItemFromLocalStorage,
  removeItemFromLocalStorage,
  setItemInLocalStorage,
} from "../../../../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { getAllJCodes } from "../../../singleOrderLayout/utils/existingDocsToLs";

import { isEqual } from "lodash";
import { usePayerNameContextFromSingleOrderLayout } from "../../../../context/payerNameContextFromSingleOrderLayout";
import { AuthOnFileRpaErrorType } from "../../../../enums/authOnFileRpaErrorType";
import { DropdownLocation } from "../../../../enums/dropdownLocation";
import { useAuthOnFileError } from "../../../../hooks/useAuthOnFileError";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import StatusError from "../../../singleOrderLayout/components/errorComponents/statusError";
import {
  clearAuthStatusChangeCommentsFromLocalStorage,
  removeAuthStatusChangesFromLocalStorageByJCode,
} from "../../../singleOrderLayout/utils/helperForAuthStatusCommentsInLocalStorage";
import { triggerDraftSubmissionOnSave } from "./actionHandlers/handleDraftSubmissionOnSave";
import {
  CptCodesTableColumnsKeys,
  generateCptCodesTableData,
} from "./cptCodesTable";
import { checkIfNewDataIsForAuthReviewStatus } from "./utils/checkIfNewDataIsForAuthReviewStatus";
import { handleValueChangeOfTextField } from "./utils/handleValueChangeOfTextField";
import { hasAnyError } from "./utils/hasTableAnyError";
import { revertTableToLastStateOnClose } from "./utils/revertTableToLastStateIfOnClose";
import {
  getCommentTypeForStatusUpdateModal,
  getDisplayTextForStatusUpdateModal,
  getDropDownOptions,
  shouldOpenHoldModal,
  shouldOpenStatusUpdateModal,
} from "./utils/shouldOpenStatusUpdateModal";
import { transformTableDataToApiPayload } from "./utils/transformRowDataForPayload";
import { updateFirestoreDocumentWithComments } from "./utils/updateFirestoreDocumentWithComments";
import { updateTableDataState } from "./utils/updateTableDataState";

const CptCodes = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { singleMedicalPaOrder, isLoading } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );

  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const { openedModalId } = useSelector((state: RootState) => state.modal);
  const [tableData, setTableData] = useState<CptCodesTableRowModel[]>([]);
  const [originalTableData, setOriginalTableData] = useState<
    CptCodesTableRowModel[]
  >([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedRowData, setSelectedRowData] =
    useState<CptCodesTableRowModel | null>(null);
  const [isHoldSaving, setIsHoldSaving] = useState(false);
  const selectedRowDataForReference = useRef<{
    rowIndex: number;
    fieldKey: string;
    fieldValue: any;
  } | null>(null);

  const dispatch = useDispatch<AppDispatch>();
  const { setHasCptTableAnyError, hasCptTableAnyError } =
    useBusniessOfficeNotesContext();

  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  // Check if BO value is "required" (case-insensitive) to show Medical Necessity flow
  // Check both locations: status.bo_status (Firestore) and bo_status (Algolia/top-level)
  const shouldShowMedicalNecessityFlow = useMemo(() => {
    const boValue =
      singleMedicalPaOrder?.status?.bo_status ||
      (singleMedicalPaOrder as any)?.bo_status;
    return boValue?.toLowerCase() === "required";
  }, [singleMedicalPaOrder]);
  const saveTableDataToLocalStorage = (data: CptCodesTableRowModel[]) => {
    try {
      const key = `${LocalStorageKeys.CPT_CODES}_${orderId}`;
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      // Handle localStorage error silently
    }
  };

  const handleHoldModal = (rowIndex: number, newValue: PaRequired): void => {
    const shouldOpenModal = shouldOpenHoldModal(newValue?.currentStatusId);
    if (!shouldOpenModal) return;
    dispatch(
      setOpenedModalId({
        id: ModalId.STATUS_CHANGED_TO_HOLD_MODAL,
      }),
    );
  };

  useEffect(() => {
    if (tableData && tableData.length > 0) {
      const jCodes = tableData.map(
        (row) => row[CptCodeWithDetailsKeys.JCodes]?.data,
      );
      setItemInLocalStorage(
        `${LocalStorageKeys.J_CODES}_${singleMedicalPaOrder?.id}`,
        jCodes,
      );
    }
  }, [tableData]);

  useEffect(() => {
    if (singleMedicalPaOrder) {
      let generatedData = generateCptCodesTableData(
        singleMedicalPaOrder,
        authStatusOptions ?? [],
      );

      const dataFromLocalStorage = getItemFromLocalStorage(
        `${LocalStorageKeys.CPT_CODES}_${orderId}`,
      );

      if (
        dataFromLocalStorage &&
        Array.isArray(dataFromLocalStorage) &&
        dataFromLocalStorage.length === generatedData.length
      ) {
        setTableData(dataFromLocalStorage as CptCodesTableRowModel[]);
        const hasAnyChanges = !isEqual(dataFromLocalStorage, generatedData);
        setHasChanges(hasAnyChanges);
        setOriginalTableData(generatedData as CptCodesTableRowModel[]);
      } else {
        setTableData(generatedData as CptCodesTableRowModel[]);
        setOriginalTableData(generatedData as CptCodesTableRowModel[]);
        setHasChanges(false);
      }
    }
  }, [singleMedicalPaOrder, orderId, authStatusOptions]);

  useEffect(() => {
    if (openedModalId === ModalId.STATUS_UPDATE_MODAL) {
      openModal(ModalId.STATUS_UPDATE_MODAL);
    }

    if (openedModalId === ModalId.UPDATE_CIG_NOTE_CONFIRMATION_DIALOG) {
      openModal(ModalId.UPDATE_CIG_NOTE_CONFIRMATION_DIALOG);
    }
    if (openedModalId === ModalId.AUTH_INACCURACY_MODAL) {
      openModal(ModalId.AUTH_INACCURACY_MODAL);
    }
    if (openedModalId === ModalId.DRAFT_SUBMISSION_MODAL) {
      openModal(ModalId.DRAFT_SUBMISSION_MODAL);
    }
  }, [openedModalId]);

  // Helper function to handle status update modal logic
  const handleStatusUpdateModal = (rowIndex: number, newValue: any): void => {
    const currentStatusId =
      tableData[rowIndex]?.[CptCodeWithDetailsKeys.PaRequired]?.currentStatusId;
    const shouldOpenModal = shouldOpenStatusUpdateModal(
      currentStatusId,
      newValue?.currentStatusId,
    );

    if (!shouldOpenModal) return;

    const displayText = getDisplayTextForStatusUpdateModal(
      newValue?.currentStatusId,
    );

    dispatch(
      setOpenedModalId({
        id: ModalId.STATUS_UPDATE_MODAL,
        metaData: {
          options: getDropDownOptions(
            currentStatusId,
            newValue?.currentStatusId,
          ),
          label: displayText?.label,
          placeholder: displayText?.placeholder,
          commentType: getCommentTypeForStatusUpdateModal(
            newValue?.currentStatusId,
          ),
        },
      }),
    );
  };

  // Main function to handle table row updates
  const handleTableRowUpdate = (
    rowIndex: number,
    fieldKey: string,
    newValue: any,
  ) => {
    let valueToUpdate = newValue;
    const dataFromLocalStorage = getItemFromLocalStorage(
      `${LocalStorageKeys.CPT_CODES}_${orderId}`,
    );
    removeAuthStatusChangesFromLocalStorageByJCode(
      singleMedicalPaOrder?.id ?? "",
      tableData?.[rowIndex]?.[CptCodeWithDetailsKeys.JCodes]?.data ?? "",
    );
    // Handle PA Required field updates (complex object structure)
    if (fieldKey === CptCodeWithDetailsKeys.PaRequired) {
      valueToUpdate = {
        text: newValue?.text,
        textColor: newValue?.color,
        bgColor: newValue?.bgColor,
        currentStatusId: newValue?.currentStatusId ?? "",
      };
      selectedRowDataForReference.current = {
        fieldKey: fieldKey,
        rowIndex: rowIndex,
        fieldValue: dataFromLocalStorage
          ? (dataFromLocalStorage?.[rowIndex]?.[
              CptCodeWithDetailsKeys.PaRequired
            ] ?? {})
          : (originalTableData[rowIndex]?.[CptCodeWithDetailsKeys.PaRequired] ??
            {}),
      };
      setSelectedRowData({
        ...tableData[rowIndex],
        paRequired: valueToUpdate,
      });
      checkIfNewDataIsForAuthReviewStatus(valueToUpdate, dispatch);
      handleStatusUpdateModal(rowIndex, valueToUpdate);
      handleHoldModal(rowIndex, newValue);
    }

    // Handle all other field updates (simple value assignment)
    updateTableDataState(
      rowIndex,
      fieldKey,
      valueToUpdate,
      singleMedicalPaOrder ?? {},
      originalTableData,
      setTableData,
      setHasChanges,
      saveTableDataToLocalStorage,
    );
  };

  const handleSaveHold = async (selectedDate: Date) => {
    setIsHoldSaving(true);
    try {
      await FirestoreService.updateDocument(
        FirestoreCollectionReference.medicalPaOrders(),
        singleMedicalPaOrder?.id || "",
        {
          "timestamps.date_of_hold_until": moment(selectedDate).format(
            "YYYY-MM-DDTHH:mm:ss.SSSSSS[+00:00]",
          ),
        },
      );
      dispatch(closeModal());
      controlToastState("hold-saved-successfully");
    } catch (error) {
      controlToastState("hold-save-failed");
    } finally {
      setIsHoldSaving(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!hasChanges) {
      controlToastState("no-changes-to-save");
      return;
    }
    try {
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id ?? "",
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: getOrgIdForFetchExternalWorklist() || "",
        event_name: EventsName.UPDATE_J_CODES_AUTH_STATUS_INITIATED,
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.CPT_CODE_SCREEN,
          source: ScreenNamesForBigQuery.CPT_CODE_SCREEN,
          tableData: tableData,
          payer_name: payerName,
        },
      });
      setIsSaving(true);
      await updateFirestoreDocumentWithComments(orderId ?? "");
      const payload = transformTableDataToApiPayload(
        tableData,
        authStatusOptions ?? [],
        orderId ?? "",
      );
      await updateCPTtables(payload);
      setOriginalTableData([...tableData]);
      setHasChanges(false);
      removeItemFromLocalStorage(`${LocalStorageKeys.BO_COMMENTS}_${orderId}`);
      removeItemFromLocalStorage(`${LocalStorageKeys.CPT_CODES}_${orderId}`);
      clearAuthStatusChangeCommentsFromLocalStorage(orderId ?? "");
      dispatch(closeModal());
      controlToastState("cpt-codes-updated-successfully");
    } catch (error) {
      controlToastState("cpt-codes-updated-failure");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveLog = () => {
    apiWorkerManager.enqueueAndProcess({
      patient_id: singleMedicalPaOrder?.patient_id ?? "",
      order_id: singleMedicalPaOrder?.order_id ?? "",
      org_id: getOrgIdForFetchExternalWorklist() || "",
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      event_name: EventsName.SAVE_BUTTON_CLICKED,
      additional_data: {
        screen_name: ScreenNamesForBigQuery.CPT_CODE_SCREEN,
        source: tableData,
        auth_status: singleMedicalPaOrder?.auth_on_file?.auth_status ?? "",
        payer_name: payerName,
      },
    });
  };

  // const renderTextWithProperIndentation = (text: string) => {
  //   const formattedText = (text ?? "N/A").replace(/\\n/g, "\n");
  //   return (
  //     <>
  //       {formattedText.split(/\n{2,}/).map((paragraph, index) => (
  //         <p key={index} className="mb-4">
  //           {paragraph.split("\n").map((line, i, arr) => (
  //             <React.Fragment key={i}>
  //               {line}
  //               {i < arr.length - 1 && <br />}
  //             </React.Fragment>
  //           ))}
  //         </p>
  //       ))}
  //     </>
  //   );
  // };

  // const getAllEvidenceText = useMemo(() => {
  //   // Special case for specific order ID
  //   if (orderId === "7d5b393e-83de-4380-b5e4-90c82acc4620") {
  //     return "Lupron Depot : C61& Case has been approved for J9217 with PA#242811925  for 4 visits from 10/05/2024 to 10/05/2025 as per portal_SN (ID-74162647700) under Leiton Chavarria, Jose";
  //   }

  //   if (singleMedicalPaOrder?.auth_on_file) {
  //     const authEntries = singleMedicalPaOrder?.auth_on_file?.auth_entries;
  //     if (authEntries) {
  //       return authEntries.map((entry) => entry.evidences).join("\n\n");
  //     }
  //   }
  //   return "";
  // }, [singleMedicalPaOrder, orderId]);

  useEffect(() => {
    if (openedModalId === ModalId.STATUS_CHANGED_TO_HOLD_MODAL) {
      openModal(ModalId.STATUS_CHANGED_TO_HOLD_MODAL);
    }
  }, [openedModalId]);

  useEffect(() => {
    setHasCptTableAnyError(hasAnyError(tableData));
  }, [tableData]);

  const {
    shouldShowError: shouldShowRegimenNotFound,
    errorMessage,
    dropdownLocation,
  } = useAuthOnFileError(
    singleMedicalPaOrder,
    AuthOnFileRpaErrorType.REGIMEN_NOT_FOUND,
  );

  const shouldShowDropdownInCpt =
    dropdownLocation === DropdownLocation.CPT ||
    dropdownLocation === DropdownLocation.BOTH;

  return (
    <div className="cpt-codes-table--container flex h-full flex-col">
      {isLoading ? (
        <LoaderMessage message={"Fetching CPT Codes"} />
      ) : shouldShowRegimenNotFound ? (
        <StatusError
          title={errorMessage}
          showDropdown={shouldShowDropdownInCpt}
        />
      ) : (
        <>
          {/* Commenting out this information box which shows the evidence text for now as we are not using it */}
          {/* <div className="table-drug-template rounded-lg bg-primaryGray-16 p-3 text-h12">
            {renderTextWithProperIndentation(getAllEvidenceText ?? "")}
            </div> */}
          <div className="cpt-codes-table--container flex h-full flex-col">
            <div className="cpt-codes-table--container--table flex-1 overflow-auto">
              <CustomTable
                tableHeaders={CptCodesTableColumnsKeys}
                tableData={tableData}
                itemsPerPage={tableData.length}
                pagesPerView={5}
                count={tableData.length}
                onRowClick={() => {}}
                tableName="cptCodes"
                hideSearchBar={true}
                hidePagination={true}
                onTableRowUpdate={handleTableRowUpdate}
                onValueChangeOfTextField={(value, id) =>
                  handleValueChangeOfTextField(
                    value,
                    id,
                    CptCodeWithDetailsKeys.JCodes,
                    tableData,
                    originalTableData,
                    setTableData,
                    setHasChanges,
                    saveTableDataToLocalStorage,
                    singleMedicalPaOrder ?? ({} as MedicalPaOrder),
                  )
                }
              />
            </div>
            {openedModalId === ModalId.UPDATE_CIG_NOTE_CONFIRMATION_DIALOG && (
              <UpdateCigNoteConfirmationDialog
                onSave={handleSaveChanges}
                onClose={() => {
                  dispatch(closeModal());
                }}
                onCancel={() => {
                  dispatch(closeModal());
                }}
                loading={isSaving}
                source="j_codes"
              />
            )}
          </div>
        </>
      )}
      {openedModalId === ModalId.STATUS_UPDATE_MODAL && (
        <StatusUpdateModal
          onClose={() => {
            dispatch(closeModal());
          }}
          rowData={selectedRowData ?? undefined}
        />
      )}
      {openedModalId === ModalId.AUTH_INACCURACY_MODAL && (
        <AuthInaccuracyModal
          onClose={() => {
            dispatch(closeModal());
          }}
          orderData={singleMedicalPaOrder}
        />
      )}
      {openedModalId === ModalId.STATUS_CHANGED_TO_HOLD_MODAL && (
        <StatusChangedToHoldModal
          onClose={() => {
            dispatch(closeModal());
          }}
          onSave={handleSaveHold}
          loading={isHoldSaving}
        />
      )}
      {openedModalId === ModalId.DRAFT_SUBMISSION_MODAL && (
        <DraftSubmissionModal
          jCodes={getAllJCodes(singleMedicalPaOrder ?? {}) ?? []}
          selectedJCodeId={selectedRowData?.jCodeId ?? ""}
          onClose={() => {
            revertTableToLastStateOnClose({
              rowIndex: selectedRowDataForReference.current?.rowIndex ?? 0,
              fieldKey: selectedRowDataForReference.current?.fieldKey ?? "",
              fieldValue: selectedRowDataForReference.current?.fieldValue ?? {},
              singleMedicalPaOrder: singleMedicalPaOrder ?? {},
              originalTableData: originalTableData,
              setTableData: setTableData,
              setHasChanges: setHasChanges,
              saveTableDataToLocalStorage: saveTableDataToLocalStorage,
            });
            dispatch(closeModal());
          }}
          onSave={(formData, jCodes, portalConfigData) => {
            triggerDraftSubmissionOnSave(
              orderId ?? "",
              formData,
              jCodes,
              portalConfigData,
              singleMedicalPaOrder ?? {},
              setIsSaving,
              dispatch,
              tableData,
              authStatusOptions ?? [],
            );
          }}
          onCancel={() => {
            revertTableToLastStateOnClose({
              rowIndex: selectedRowDataForReference.current?.rowIndex ?? 0,
              fieldKey: selectedRowDataForReference.current?.fieldKey ?? "",
              fieldValue: selectedRowDataForReference.current?.fieldValue ?? {},
              singleMedicalPaOrder: singleMedicalPaOrder ?? {},
              originalTableData: originalTableData,
              setTableData: setTableData,
              setHasChanges: setHasChanges,
              saveTableDataToLocalStorage: saveTableDataToLocalStorage,
            });
            dispatch(closeModal());
          }}
          orderId={orderId ?? ""}
          isSaveDisabled={isSaving}
        />
      )}
    </div>
  );
};

export default CptCodes;
